import React, { useRef } from 'react';
import { useEcho } from '@laravel/echo-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';
import { UserNotification } from '@api/notifications/interfaces';
import { useAuth } from '@context/AuthProvider';
import { useBroadcasting } from '@hooks/useBroadcasting';
import { useOpenNotification } from '@hooks/useOpenNotification';
import { getCurrentMember } from '@api/memberApi';
import { myNotificationsQueryKey } from '@reactQuery/notifications';
import { CURRENT_USER_QUERY_KEY } from '@reactQuery/auth';
import { getNotificationLabel } from '@notifications/registry';
import { SHOOTING_CLEARANCE_ME_REFETCH_NOTIFICATION_TYPES } from '@notifications/shootingClearanceNotificationTypes';
import {
  INotificationChannelListener,
  INotificationLiveUpdates,
} from './interfaces';

const NotificationChannelListener: React.FC<INotificationChannelListener> = ({
  userId,
}): null => {
  const { channel } = useBroadcasting();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const openNotification = useOpenNotification();
  const openNotificationRef = useRef(openNotification);
  const labelRef = useRef(t);

  openNotificationRef.current = openNotification;
  labelRef.current = t;

  useEcho<UserNotification>(
    channel(`user.${userId}.notifications`),
    '.notification.created',
    (notification) => {
      void queryClient.invalidateQueries({
        queryKey: myNotificationsQueryKey,
      });

      if (SHOOTING_CLEARANCE_ME_REFETCH_NOTIFICATION_TYPES.has(notification.type)) {
        void queryClient.fetchQuery({
          queryKey: CURRENT_USER_QUERY_KEY,
          queryFn: getCurrentMember,
        });
      }

      let snackbarId: SnackbarKey;

      snackbarId = enqueueSnackbar(
        getNotificationLabel(notification, labelRef.current),
        {
          variant: 'info',
          SnackbarProps: {
            style: { cursor: 'pointer' },
            onClick: () => {
              closeSnackbar(snackbarId);
              void openNotificationRef.current(notification);
            },
          },
        }
      );
    }
  );

  return null;
};

export const NotificationLiveUpdates: React.FC<
  INotificationLiveUpdates
> = (): JSX.Element | null => {
  const { currentUser } = useAuth();

  if (!currentUser?.id || !currentUser.broadcasting) {
    return null;
  }

  return <NotificationChannelListener userId={currentUser.id} />;
};
